import { AxisMode, AxisType } from "@focus/focusGrafos/lib/interfaces/axis";
import {
  FolderType,
  GraphCommand,
  GraphType,
} from "@focus/focusGrafos/lib/interfaces/config";
import type { GrafosData } from "@focus/focusGrafos/lib/interfaces/data";
import {
  GrafosSymbol,
  LineStyle,
} from "@focus/focusGrafos/lib/interfaces/element";
import {
  ObjectType,
  type GrafosObject,
} from "@focus/focusGrafos/lib/interfaces/object";
import { withTimeSystem } from "../../constants/orbitMetadata";
import type { EventEvolutionEvent } from "../events/EventEvolutionTypes";
import {
  EVENT_EVOLUTION_METRICS,
  formatMetricThresholdValue,
  parseEventNumber,
  type EventMetricDefinition,
} from "../events/eventEvolutionUtils";

const EVOLUTION_FILE = "event-evolution.plot";
const MJD2000_UNIX_EPOCH = Date.UTC(2000, 0, 1, 0, 0, 0);
const MILLIS_PER_DAY = 86400 * 1000;
const DEFAULT_TCA_MJD2000 = 9201.730127314815;
const evolutionSeries = EVENT_EVOLUTION_METRICS;

interface EvolutionPlotModelParams {
  criticalRecord?: EventEvolutionEvent;
  records?: EventEvolutionEvent[];
  tca?: string;
  defRiskLevel?: Record<string, unknown>;
}

/**
 * Parses an execution time into MJD2000.
 *
 * This parser intentionally returns null for invalid record dates instead of
 * falling back to DEFAULT_TCA_MJD2000. Otherwise, all invalid records would be
 * plotted at the same X coordinate.
 */
const parseExecutionTimeToMjd2000 = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const strictMatch = value.match(
    /^(\d{4})\/(\d{2})\/(\d{2})-(\d{2}):(\d{2}):(\d{2})$/,
  );

  if (strictMatch) {
    const [, year, month, day, hour, minute, second] = strictMatch.map(Number);

    const timestamp = Date.UTC(year, month - 1, day, hour, minute, second);

    return (timestamp - MJD2000_UNIX_EPOCH) / MILLIS_PER_DAY;
  }

  /**
   * Fallback for ISO-like dates or other formats parseable by JS:
   * - 2025-03-12T14:30:00Z
   * - 2025-03-12 14:30:00
   *
   * The preferred format is still YYYY/MM/DD-HH:mm:ss.
   */
  const timestamp = Date.parse(value);

  if (Number.isNaN(timestamp)) {
    console.warn("Invalid executionTime for evolution plot:", value);
    return null;
  }

  return (timestamp - MJD2000_UNIX_EPOCH) / MILLIS_PER_DAY;
};

/**
 * Keeps previous public behaviour for TCA-like values:
 * if the value cannot be parsed, returns DEFAULT_TCA_MJD2000.
 */
export const toMjd2000 = (value?: string) =>
  parseExecutionTimeToMjd2000(value) ?? DEFAULT_TCA_MJD2000;

export const mjd2000ToUnix = (value: number) =>
  value * MILLIS_PER_DAY + MJD2000_UNIX_EPOCH;

const getSortedRecords = (records: EventEvolutionEvent[]) =>
  [...records].sort((a, b) => {
    const aTime = parseExecutionTimeToMjd2000(a.executionTime);
    const bTime = parseExecutionTimeToMjd2000(b.executionTime);

    if (aTime === null && bTime === null) return 0;
    if (aTime === null) return 1;
    if (bTime === null) return -1;

    return aTime - bTime;
  });

const getRecordsWithValidExecutionTime = (records: EventEvolutionEvent[]) =>
  records.filter(
    (record) => parseExecutionTimeToMjd2000(record.executionTime) !== null,
  );

const createGraphObject = (
  graphId: string,
  object: Partial<GrafosObject>,
): GrafosObject => ({
  graphId,
  title: "",
  fillObject: true,
  color: "red",
  command: "create-obj",
  lineWidth: 1,
  lineStyle: LineStyle.solid,
  type: ObjectType.hband,
  coords: [],
  condition: "",
  file: "",
  colX: "",
  colY: "",
  coordsString: "",
  text: "",
  ...object,
});

const createTcaObject = (graphId: string, tcaMjd2000: number) =>
  createGraphObject(graphId, {
    title: withTimeSystem("TCA"),
    color: "#FF0000",
    lineWidth: 5,
    type: ObjectType.vertical,
    coords: [tcaMjd2000],
    info: {
      label: withTimeSystem("TCA"),
      value: "Time of Closest Approach",
    },
  });

const PROBABILITY_SERIES_IDS = new Set(["poc", "mpoc", "spoc"]);

const normalizeSeriesId = (id: string) => id.toLowerCase();

const isProbabilitySeries = (series: EventMetricDefinition) =>
  PROBABILITY_SERIES_IDS.has(normalizeSeriesId(series.id));

const isUpperRiskSeries = (series: EventMetricDefinition) =>
  series.thresholdMode === "upper" || isProbabilitySeries(series);

const finiteBandCoords = (from: number, to: number) =>
  [from, to] as unknown as number[];

/**
 * focusGrafos hband semantics:
 * [from, null] means from `from` to the upper visible bound of the Y axis.
 */
const upperInfiniteBandCoords = (from: number) =>
  [from, null] as unknown as number[];

const getThresholdCoords = (
  records: EventEvolutionEvent[],
  series: EventMetricDefinition,
  threshold: number,
) => {
  if (series.thresholdMode === "absolute") {
    const limit = Math.abs(threshold);

    return [-limit, limit];
  }

  if (isUpperRiskSeries(series)) {
    return upperInfiniteBandCoords(threshold);
  }

  return [0, threshold];
};

const getDefRiskThresholdForSeries = (
  defRiskLevel: Record<string, unknown> | undefined,
  series: EventMetricDefinition,
) => {
  if (!defRiskLevel) return { warning: null, alert: null };

  const keyMap: Record<string, string> = {
    minDistance: "minDist",
    radial: "radDist",
    along: "alongDist",
    cross: "crossDist",
    poc: "prob",
    mpoc: "prob",
    spoc: "prob",
  };

  const key = keyMap[series.id] ?? series.id;

  const warningLevel = defRiskLevel.warningLevel as
    | Record<string, unknown>
    | undefined;

  const alertLevel = defRiskLevel.alertLevel as
    | Record<string, unknown>
    | undefined;

  const warning = parseEventNumber(warningLevel?.[key]);
  const alert = parseEventNumber(alertLevel?.[key]);

  return { warning, alert };
};

const createSymmetricRiskBandObjects = (
  graphId: string,
  series: EventMetricDefinition,
  warningThreshold: number | null,
  alertThreshold: number | null,
): GrafosObject[] => {
  const objects: GrafosObject[] = [];

  const warningLimit =
    warningThreshold !== null ? Math.abs(warningThreshold) : null;

  const alertLimit = alertThreshold !== null ? Math.abs(alertThreshold) : null;

  /**
   * Symmetric absolute metrics:
   *
   * Example: radial / along / cross
   *
   * -warning -------- -alert -------- 0 -------- alert -------- warning
   *    yellow            red              red          yellow
   */
  if (warningLimit !== null && alertLimit !== null) {
    const innerLimit = Math.min(warningLimit, alertLimit);
    const outerLimit = Math.max(warningLimit, alertLimit);

    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Warning)`,
        color: "yellow",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(-outerLimit, -innerLimit),
        info: {
          label: `${series.thresholdTitle} (Warning)`,
          value: formatMetricThresholdValue(series, String(warningThreshold)),
        },
      }),
    );

    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Warning)`,
        color: "yellow",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(innerLimit, outerLimit),
        info: {
          label: `${series.thresholdTitle} (Warning)`,
          value: formatMetricThresholdValue(series, String(warningThreshold)),
        },
      }),
    );

    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Alert)`,
        color: "red",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(-innerLimit, innerLimit),
        info: {
          label: `${series.thresholdTitle} (Alert)`,
          value: formatMetricThresholdValue(series, String(alertThreshold)),
        },
      }),
    );

    return objects;
  }

  if (warningLimit !== null) {
    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Warning)`,
        color: "yellow",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(-warningLimit, warningLimit),
        info: {
          label: `${series.thresholdTitle} (Warning)`,
          value: formatMetricThresholdValue(series, String(warningThreshold)),
        },
      }),
    );
  }

  if (alertLimit !== null) {
    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Alert)`,
        color: "red",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(-alertLimit, alertLimit),
        info: {
          label: `${series.thresholdTitle} (Alert)`,
          value: formatMetricThresholdValue(series, String(alertThreshold)),
        },
      }),
    );
  }

  return objects;
};

const createRiskBandObjects = (
  graphId: string,
  records: EventEvolutionEvent[],
  series: EventMetricDefinition,
  warningThreshold: number | null,
  alertThreshold: number | null,
): GrafosObject[] => {
  const objects: Array<GrafosObject | null> = [];

  /**
   * Upper-risk metrics:
   *
   * Example: PoC / MPoC / SPoC
   *
   * 0 -------- warning -------- alert -------- upper visible bound
   *      none        yellow          red
   */
  if (isUpperRiskSeries(series)) {
    if (warningThreshold !== null && alertThreshold !== null) {
      objects.push(
        createGraphObject(graphId, {
          title: `${series.thresholdTitle} (Warning)`,
          color: "yellow",
          lineWidth: 1,
          type: ObjectType.hband,
          coords: finiteBandCoords(warningThreshold, alertThreshold),
          info: {
            label: `${series.thresholdTitle} (Warning)`,
            value: formatMetricThresholdValue(series, String(warningThreshold)),
          },
        }),
      );
    } else if (warningThreshold !== null) {
      objects.push(
        createGraphObject(graphId, {
          title: `${series.thresholdTitle} (Warning)`,
          color: "yellow",
          lineWidth: 1,
          type: ObjectType.hband,
          coords: upperInfiniteBandCoords(warningThreshold),
          info: {
            label: `${series.thresholdTitle} (Warning)`,
            value: formatMetricThresholdValue(series, String(warningThreshold)),
          },
        }),
      );
    }

    if (alertThreshold !== null) {
      objects.push(
        createGraphObject(graphId, {
          title: `${series.thresholdTitle} (Alert)`,
          color: "red",
          lineWidth: 1,
          type: ObjectType.hband,
          coords: upperInfiniteBandCoords(alertThreshold),
          info: {
            label: `${series.thresholdTitle} (Alert)`,
            value: formatMetricThresholdValue(series, String(alertThreshold)),
          },
        }),
      );
    }

    return objects.filter((o): o is GrafosObject => o !== null);
  }

  /**
   * Absolute/symmetric metrics:
   *
   * Example: radial / along / cross
   *
   * Avoids warning-alert overlap:
   * - Alert: central red band.
   * - Warning: two yellow side bands.
   */
  if (series.thresholdMode === "absolute") {
    return createSymmetricRiskBandObjects(
      graphId,
      series,
      warningThreshold,
      alertThreshold,
    );
  }

  /**
   * Lower-risk metrics:
   *
   * Example: minDistance
   *
   * 0 -------- alert -------- warning -------- upper
   *     red          yellow          none
   */
  if (alertThreshold !== null) {
    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Alert)`,
        color: "red",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(0, alertThreshold),
        info: {
          label: `${series.thresholdTitle} (Alert)`,
          value: formatMetricThresholdValue(series, String(alertThreshold)),
        },
      }),
    );
  }

  if (warningThreshold !== null && alertThreshold !== null) {
    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Warning)`,
        color: "yellow",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(alertThreshold, warningThreshold),
        info: {
          label: `${series.thresholdTitle} (Warning)`,
          value: formatMetricThresholdValue(series, String(warningThreshold)),
        },
      }),
    );
  } else if (warningThreshold !== null) {
    objects.push(
      createGraphObject(graphId, {
        title: `${series.thresholdTitle} (Warning)`,
        color: "yellow",
        lineWidth: 1,
        type: ObjectType.hband,
        coords: finiteBandCoords(0, warningThreshold),
        info: {
          label: `${series.thresholdTitle} (Warning)`,
          value: formatMetricThresholdValue(series, String(warningThreshold)),
        },
      }),
    );
  }

  return objects.filter((o): o is GrafosObject => o !== null);
};

const createCriticalPointObject = (
  graphId: string,
  criticalRecord: EventEvolutionEvent | undefined,
  series: EventMetricDefinition,
) => {
  const value = parseEventNumber(criticalRecord?.[series.id]);
  const executionTime = parseExecutionTimeToMjd2000(
    criticalRecord?.executionTime,
  );

  if (value === null || executionTime === null) {
    return null;
  }

  return createGraphObject(graphId, {
    title: "Critical record",
    color: "red",
    lineWidth: 5,
    type: ObjectType.point,
    coords: [executionTime, value],
    info: {
      label: "Critical record",
      value: criticalRecord?.recordId ?? "Critical record",
    },
  });
};

const createGraphObjects = (
  graphId: string,
  tcaMjd2000: number,
  series: EventMetricDefinition,
  records: EventEvolutionEvent[],
  criticalRecord: EventEvolutionEvent | undefined,
  defRiskLevel?: Record<string, unknown>,
): GrafosObject[] => {
  const objs: GrafosObject[] = [];

  // Prefer default risk levels if provided: warning + alert bands.
  const { warning, alert } = getDefRiskThresholdForSeries(defRiskLevel, series);

  if (warning !== null || alert !== null) {
    objs.push(
      ...createRiskBandObjects(graphId, records, series, warning, alert),
    );
  } else {
    // Fallback to event-specific single threshold. Keeps previous behaviour.
    const threshold = parseEventNumber(criticalRecord?.[series.thresholdId]);

    if (threshold !== null) {
      objs.push(
        createGraphObject(graphId, {
          title: series.thresholdTitle,
          color: "red",
          lineWidth: 1,
          type: ObjectType.hband,
          coords: getThresholdCoords(records, series, threshold),
          info: {
            label: series.thresholdTitle,
            value: formatMetricThresholdValue(series, String(threshold)),
          },
        }),
      );
    }
  }

  const critical = createCriticalPointObject(graphId, criticalRecord, series);
  if (critical) objs.push(critical);

  objs.push(createTcaObject(graphId, tcaMjd2000));

  return objs;
};

const createEvolutionTab = (
  series: EventMetricDefinition,
  index: number,
  objects: GrafosObject[],
) => {
  const probabilitySeries = isProbabilitySeries(series);
  const graphId = `GRAPH${index}`;

  return {
    title: series.label,
    id: `TAB_${series.id}`,
    command: "tab-graph",
    struct: { type: "item" as const, content: graphId },
    graphs: [
      {
        id: graphId,
        grid: true,
        command: GraphCommand.timegraph,
        title: series.graphTitle,
        type: GraphType.Normal,
        timeline: true,
        elements: [
          {
            file: EVOLUTION_FILE,
            colX: "executionTime",
            colY: series.id,
            colY2: "",
            colZ: "",
            colTime: "executionTime",
            graphId,
            title: series.label,
            command: "create-data",
            legendFromFile: false,
            symbolSize: 3,
            fillSymbol: true,
            symbol: GrafosSymbol.circle,
            symbolColor: series.color,
            lineWidth: 3,
            lineStyle: LineStyle.solid,
          },
        ],
        axes: [
          {
            id: `${graphId}_X`,
            command: "conf-axis",
            title: withTimeSystem("Execution Epoch"),
            type: AxisType.x,
            mode: AxisMode.Date,
            minValue: null,
            maxValue: null,
            width: 1,
            color: "black",
          },
          {
            id: `${graphId}_Y`,
            command: "conf-axis",
            title: series.axisTitle,
            type: AxisType.y,
            mode: probabilitySeries ? AxisMode.Logarithmic : AxisMode.Linear,
            minValue: probabilitySeries ? 0 : series.minValue,
            maxValue: series.maxValue,
            width: 1,
            color: "black",
          },
        ],
        objects,
        surfaces: [],
      },
    ],
  };
};

const createPlotObjects = (
  tcaMjd2000: number,
  records: EventEvolutionEvent[],
  criticalRecord: EventEvolutionEvent | undefined,
  defRiskLevel?: Record<string, unknown>,
) =>
  evolutionSeries.flatMap((series, index) =>
    createGraphObjects(
      `GRAPH${index}`,
      tcaMjd2000,
      series,
      records,
      criticalRecord,
      defRiskLevel,
    ),
  );

const createPlotConfig = (
  tcaMjd2000: number,
  records: EventEvolutionEvent[],
  criticalRecord: EventEvolutionEvent | undefined,
  defRiskLevel?: Record<string, unknown>,
) => ({
  format: "Bahn",
  customSelectorFolderList: [],
  selectorFolderList: [
    {
      id: "",
      title: "",
      type: FolderType.Defined,
      selectorList: [
        {
          id: "EVENT_EVOLUTION",
          title: "Event Evolution",
          state: false,
          tabs: evolutionSeries.map((series, index) =>
            createEvolutionTab(
              series,
              index,
              createGraphObjects(
                `GRAPH${index}`,
                tcaMjd2000,
                series,
                records,
                criticalRecord,
                defRiskLevel,
              ),
            ),
          ),
        },
      ],
    },
  ],
});

const createPlotData = (
  records: EventEvolutionEvent[],
  tcaMjd2000: number,
  criticalRecord: EventEvolutionEvent | undefined,
  defRiskLevel?: Record<string, unknown>,
): GrafosData => {
  const sortedRecords = getSortedRecords(records);
  const validRecords = getRecordsWithValidExecutionTime(sortedRecords);

  const data = evolutionSeries.reduce<Record<string, Array<number | null>>>(
    (acc, series) => ({
      ...acc,
      [`${series.id}-${EVOLUTION_FILE}`]: validRecords.map((record) =>
        parseEventNumber(record[series.id]),
      ),
    }),
    {
      [`executionTime-${EVOLUTION_FILE}`]: validRecords.map((record) =>
        parseExecutionTimeToMjd2000(record.executionTime),
      ),
    },
  );

  return {
    data: data as GrafosData["data"],
    objects: createPlotObjects(
      tcaMjd2000,
      validRecords,
      criticalRecord,
      defRiskLevel,
    ),
  };
};

export const createEvolutionPlotModel = ({
  criticalRecord,
  records = [],
  tca,
  defRiskLevel,
}: EvolutionPlotModelParams) => {
  const tcaMjd2000 = toMjd2000(tca);
  const sortedRecords = getSortedRecords(records);
  const validRecords = getRecordsWithValidExecutionTime(sortedRecords);

  const criticalPlotRecord = criticalRecord ?? validRecords[0];

  return {
    plotConfig: createPlotConfig(
      tcaMjd2000,
      validRecords,
      criticalPlotRecord,
      defRiskLevel,
    ),
    plotData: createPlotData(
      validRecords,
      tcaMjd2000,
      criticalPlotRecord,
      defRiskLevel,
    ),
    tcaMjd2000,
  };
};
